import PermissionButton from '../../../permissions/PermissionButton';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tabs,
  message,
  DatePicker,
  Form,
  Tooltip,
  Modal,
  Drawer,
  Select,
} from 'antd';
import {
  SearchOutlined,
  SwapOutlined,
  FilterOutlined,
  ReloadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useFetchAllBonReceptions,
  useTransferReceptionToFacture,
  useCreateBonReception,
} from '../../../hooks/achatReceptionHooks';
import { useFetchAllFours } from '../../../hooks/fourHooks';
import { useFetchAllEntrepots, useDefaultEntrepot } from '../../../hooks/entrepotHooks';
import { getDefaultEntrepot } from '../../../utils/defaultEntrepot';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// Constants for localStorage
const ACTIVE_TAB_KEY = 'bon_reception_active_tab';

const ReceptionList = ({ onSelect }) => {
  const [fournisseur, setFournisseur] = useState('');
  const [refSearch, setRefSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [dateRange, setDateRange] = useState([null, null]);
  const [form] = Form.useForm();
  const [hoveredRow, setHoveredRow] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isTransferring, setIsTransferring] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Initialize activeTab from localStorage or default to 'notValidated'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem(ACTIVE_TAB_KEY);
    return savedTab || 'notValidated';
  });

  // Initialize sorter based on activeTab
  const [sorter, setSorter] = useState(() => {
    const savedTab = localStorage.getItem(ACTIVE_TAB_KEY) || 'notValidated';
    return {
      field: 'REF_BR_A',
      order: savedTab === 'transferred' ? 'descend' : 'ascend'
    };
  });

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab]);

  const params = useMemo(() => ({
    page: pagination.current,
    pageSize: pagination.pageSize,
    frsSearch: fournisseur || undefined,
    refSearch: refSearch || undefined,
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    sortField: sorter.field,
    sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
    status: activeTab,
  }), [pagination.current, pagination.pageSize, fournisseur, refSearch, dateRange, sorter.field, sorter.order, activeTab]);

  const { items, total, loading, refetch } = useFetchAllBonReceptions(params);
  const { transfer } = useTransferReceptionToFacture();
  const handleReceptionCreated = async (id) => {
    setIsDrawerOpen(false);
    await refetch();
    if (id) {
      onSelect(id);
    }
  };

  const rowSelection = activeTab === 'validated' ? {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.EN_FACTURE,
    }),
    onCell: () => ({
      onClick: e => {
        e.stopPropagation();
      }
    }),
  } : undefined;

  const handleRowClick = (record) => {
    onSelect(record.id);
  };

  // Small grey index column for REF_BR_A (internal reference)
  const indexColumn = {
    title: '',
    dataIndex: 'REF_BR_A',
    key: 'index',
    width: 80,
    render: (text) => (
      <span style={{ 
        fontSize: 11, 
        color: '#999', 
        fontFamily: 'monospace',
        letterSpacing: '-0.5px'
      }}>
        {text}
      </span>
    )
  };

  // Main stylish REF_BL column with search
  const refBLColumn = {
    title: 'Réf. BL Fournisseur',
    dataIndex: 'REF_BL',
    key: 'REF_BL',
    width: 180,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Rechercher Réf. BL"
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setRefSearch(selectedKeys[0]);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          style={{ marginBottom: 8, display: 'block' }}
          ref={node => node && setTimeout(() => node.focus(), 0)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setRefSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setRefSearch('');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            size="small"
            style={{ width: 90 }}
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    render: (text, record) => (
      <div style={{ position: 'relative' }}>
        <span
          style={{
            fontWeight: 600,
            fontSize: 14,
            color: hoveredRow === record.id ? '#1890ff' : '#262626',
            transition: 'all 0.2s ease',
            display: 'inline-block',
          }}
        >
          {text || <span style={{ color: '#bfbfbf', fontStyle: 'italic', fontWeight: 400 }}>—</span>}
        </span>
        <span
          style={{
            position: 'absolute',
            bottom: -2,
            left: 0,
            width: hoveredRow === record.id ? '100%' : '0%',
            height: '2px',
            backgroundColor: '#1890ff',
            transition: 'width 0.3s ease',
            borderRadius: 1,
          }}
        />
      </div>
    )
  };

  const dateColumn = {
    title: 'Date Récep.',
    dataIndex: 'DATE_BR',
    key: 'DATE_BR',
    render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '',
    sorter: true,
    onCell: () => ({
      onClick: e => {
        e.stopPropagation();
        return false;
      }
    })
  };

  const fournisseurColumn = {
    title: 'Fournisseur',
    dataIndex: 'FOURNISSEUR',
    key: 'FOURNISSEUR',
    width: 400,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Rechercher Fournisseur ou Compte"
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setFournisseur(selectedKeys[0]);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          style={{ marginBottom: 8, display: 'block' }}
          ref={node => node && setTimeout(() => node.focus(), 0)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setFournisseur(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setFournisseur('');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            size="small"
            style={{ width: 90 }}
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    render: (text, record) => (
      <span>
        {text}
        {record.fournisseur?.compte && (
          <span style={{ marginLeft: 6, color: '#8c8c8c', fontSize: 13 }}>
            ({record.fournisseur.compte})
          </span>
        )}
      </span>
    ),
  };

  const priceColumnHT = {
    title: 'Total HT',
    dataIndex: 'MNT_HT',
    key: 'MNT_HT',
    render: value => value?.toFixed(2) || '0.00',
    sorter: true,
  };

  const priceColumnTTC = {
    title: 'Total TTC',
    dataIndex: 'MNT_TTC',
    key: 'MNT_TTC',
    render: value => value?.toFixed(2) || '0.00',
    sorter: true,
  };

  const columns = [
    indexColumn,
    refBLColumn,
    dateColumn,
    fournisseurColumn,
    priceColumnHT,
    priceColumnTTC,
    {
      title: 'Remarque',
      dataIndex: 'REMARQUE',
      key: 'REMARQUE',
      ellipsis: true,
    },
  ];

  const handleBulkTransfer = async () => {
    if (!selectedRowKeys.length) {
      message.warning('Veuillez sélectionner des réceptions à transférer');
      return;
    }

    Modal.confirm({
      title: 'Confirmer le transfert',
      content: `Êtes-vous sûr de vouloir transférer ${selectedRowKeys.length} réception(s) vers des factures d'achat?`,
      okText: 'Oui, transférer',
      cancelText: 'Annuler',
      onOk: async () => {
        setIsTransferring(true);
        try {
          const results = [];
          for (const id of selectedRowKeys) {
            const res = await transfer(id);
            results.push(res);
          }
          message.success(`${selectedRowKeys.length} facture(s) créée(s) avec succès`);
          setSelectedRowKeys([]);
          refetch();
        } catch (error) {
          message.error('Échec du transfert: ' + (error.message || error));
        } finally {
          setIsTransferring(false);
        }
      }
    });
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSelectedRowKeys([]);

    // Update the sorter based on the active tab
    setSorter({
      field: 'REF_BR_A',
      order: newTab === 'transferred' ? 'descend' : 'ascend'
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter(sorter);
  };

  const getRowStyle = (record) => {
    if (record.EN_FACTURE) {
      return { backgroundColor: '#e6f7ff' };
    }
    if (activeTab === 'validated') {
      return { backgroundColor: '#f6ffed' };
    }
    return {};
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearAllFilters = () => {
    setFournisseur('');
    setRefSearch('');
    setDateRange([null, null]);
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRefetch = async () => {
    try {
      await refetch();
      message.success('Liste des réceptions actualisée');
    } catch (error) {
      message.error(`Échec de l'actualisation: ${error?.message || 'Erreur inconnue'}`);
    }
  };

  return (
    <div className="reception-list-container">
      <Card
        style={{
          marginTop: 20,
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        className="reception-card"
      >
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>
              Listes Des Bons de Réception
            </h2>
            <Tooltip title="Actualiser la liste">
              <Button
                onClick={handleRefetch}
                icon={<ReloadOutlined />}
                type="text"
              />
            </Tooltip>
          </div>
          <Space>
            {activeTab === 'validated' && selectedRowKeys.length > 0 && (
              <PermissionButton permissionModule="purchaseReceipts" permissionAction="transfer"
                type="primary"
                icon={<SwapOutlined />}
                onClick={handleBulkTransfer}
                loading={isTransferring}
              >
                Transférer Sélectionnés ({selectedRowKeys.length})
              </PermissionButton>
            )}
            <PermissionButton permissionModule="purchaseReceipts" permissionAction="create"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsDrawerOpen(true)}
            >
              Créer Réception
            </PermissionButton>
          </Space>
        </Space>

        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}
        >
          <Form.Item
            name="dateRange"
            label="Période"
            style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6, marginBottom: 4 }}
            labelCol={{ style: { display: 'flex', alignItems: 'center', paddingBottom: 0 } }}
          >
            <RangePicker
              onChange={handleDateChange}
              value={dateRange}
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
              allowClear
              size="small"
              style={{ width: 260 }}
            />
          </Form.Item>
          <Form.Item style={{ marginRight: 6, marginBottom: 4 }}>
            <Tooltip title="Réinitialiser période">
              <Button
                icon={<FilterOutlined />}
                onClick={() => {
                  setDateRange([null, null]);
                  form.resetFields(['dateRange']);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                size="small"
              />
            </Tooltip>
          </Form.Item>
        </Form>

        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Enregistrés" key="notValidated" />
          <TabPane tab="Validés" key="validated" />
          <TabPane tab="Transférés" key="transferred" />
        </Tabs>

        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            onMouseEnter: () => setHoveredRow(record.id),
            onMouseLeave: () => setHoveredRow(null),
            style: { ...getRowStyle(record), cursor: 'pointer' },
            className: 'reception-table-row',
          })}
          rowSelection={rowSelection}
          className="reception-table"
          size="small"
        />
        <Drawer
          title="Créer Nouvelle Réception"
          width={920}
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          bodyStyle={{ paddingBottom: 80 }}
        >
          <ReceptionForm onReceptionCreated={handleReceptionCreated} />
        </Drawer>
      </Card>
    </div>
  );
};

export default ReceptionList;

const ReceptionForm = ({ onReceptionCreated }) => {
  const { Fours: fours, loading: loadingFours } = useFetchAllFours();
  const { entrepots } = useFetchAllEntrepots();
  const { defaultCode } = useDefaultEntrepot();
  const { create, loading } = useCreateBonReception();
  const [selectedFour, setSelectedFour] = useState(null);
  const [selectedEntrepot, setSelectedEntrepot] = useState(null);
  const [search, setSearch] = useState('');
  const defaultAppliedRef = useRef(false);

  useEffect(() => {
    if (defaultCode && !defaultAppliedRef.current) {
      setSelectedEntrepot(defaultCode);
      defaultAppliedRef.current = true;
    }
  }, [defaultCode]);

  const data = (fours || []).filter((f) => {
    const q = (search || '').toLowerCase();
    return (
      !q ||
      f.sociale?.toLowerCase().includes(q) ||
      f.compte?.toLowerCase().includes(q)
    );
  });

  const columns = [
    { title: 'Compte', dataIndex: 'compte', key: 'compte' },
    { title: 'Fournisseur', dataIndex: 'sociale', key: 'sociale' },
    { title: 'Pays', dataIndex: 'pays', key: 'pays' },
  ];

  const handleCreate = async () => {
    if (!selectedFour) {
      message.error('Veuillez sélectionner un fournisseur');
      return;
    }
    try {
      const codeEnt = selectedEntrepot || defaultCode || (await getDefaultEntrepot()) || null;
      const payload = {
        CODE_FRS: selectedFour.code_frs,
        FOURNISSEUR: selectedFour.sociale,
        DATE_BR: new Date(),
        CODE_ENT: codeEnt,
      };
      const res = await create(payload);
      message.success(`Réception créée: ${res.REF_BR_A || res.id}`);
      if (onReceptionCreated) {
        onReceptionCreated(res.id);
      }
    } catch (error) {
      message.error(error.message || 'Erreur création');
    }
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ padding: '12px', background: '#f0f2f5', borderRadius: 6 }}>
        <div style={{ color: selectedFour ? '#000' : '#999', marginBottom: 6 }}>
          {selectedFour ? 'Fournisseur sélectionné' : 'Veuillez sélectionner un fournisseur'}
        </div>
        {selectedFour && (
          <div>
            <strong>{selectedFour.sociale}</strong> — Compte: {selectedFour.compte || 'N/A'}
          </div>
        )}
      </div>
      <Select
        placeholder="Sélectionner un entrepôt (optionnel)"
        value={selectedEntrepot}
        onChange={(value) => setSelectedEntrepot(value)}
        options={(entrepots || []).map((e) => ({
          value: e.code_ent,
          label: `${e.code_ent} - ${e.name}`,
        }))}
        allowClear
        style={{ width: '100%' }}
      />
      <Input
        placeholder="Rechercher fournisseur (compte, nom)"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <Table
        size="small"
        rowKey="code_frs"
        loading={loadingFours}
        dataSource={data}
        columns={columns}
        pagination={{ pageSize: 8 }}
        onRow={(record) => ({
          onClick: () => setSelectedFour(record),
          style: {
            cursor: 'pointer',
            backgroundColor: selectedFour?.code_frs === record.code_frs ? '#e6f7ff' : undefined,
          },
        })}
      />
      <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
        <PermissionButton permissionModule="purchaseReceipts" permissionAction="create" type="primary" onClick={handleCreate} disabled={!selectedFour} loading={loading}>
          Créer Réception
        </PermissionButton>
      </Space>
    </div>
  );
};
